<script setup lang="ts">
import { Order_By } from "@/graphql";
import { ProjectListSortOption } from "../constants/SortOptions";

import Popover from "@/components/Reusables/Popover/Popover.vue";
import { ArrowDown, ArrowDownUp, ArrowUp, Star } from "@lucide/vue";

const props = withDefaults(
  defineProps<{
    options: Array<ProjectListSortOption>;
    value: ProjectListSortOption;
    // Tooltip label shown on hover via v-tooltip.
    title?: string;
  }>(),
  { title: "Sort" }
);

const emit = defineEmits<{
  (e: "change", sortOption: ProjectListSortOption["orderBy"]): void;
}>();

// Trigger styled as a bare icon so it sits embedded inside the search input's `#actions`
// slot, matching the SortBox used in the Material Mapping toolbar.
const triggerClass =
  "rounded-8 focus-ring p-px-8 relative inline-flex h-fit shrink-0 items-center justify-center text-type-default";

const isActiveField = (field: ProjectListSortOption["orderBy"]["field"]) => props.value.orderBy.field === field;

// Clicking the active option flips its direction; clicking another option selects it with
// that option's default direction. Mirrors the SortBox asc/desc toggle interaction.
const handleSelect = (option: ProjectListSortOption) => {
  if (isActiveField(option.orderBy.field)) {
    emit("change", {
      field: option.orderBy.field,
      direction: props.value.orderBy.direction === Order_By.asc_nulls_last ? Order_By.desc_nulls_last : Order_By.asc_nulls_last,
    });
  } else {
    emit("change", option.orderBy);
  }
};
</script>

<template>
  <Popover
    placement="bottom-end"
    panel-class="w-72 flex flex-col overflow-hidden"
    :aria-label="title"
    :return-focus="false">
    <template #trigger="{ toggle }">
      <button
        v-tooltip="{ text: title, variant: 'label', placement: 'bottom' }"
        type="button"
        :aria-label="title"
        :class="triggerClass"
        @click="toggle">
        <ArrowDownUp
          class="icon-sm shrink-0"
          :stroke-width="1.5"
          color="var(--color-app-blue-800)"
          fill="var(--color-app-blue-100)" />
      </button>
    </template>

    <div class="flex items-center justify-between border-b border-slate-200 px-3 py-2">
      <span class="t-caption-bold text-type-muted uppercase">Sort by</span>
    </div>

    <div class="p-1">
      <button
        v-for="option in options"
        :key="option.orderBy.field"
        type="button"
        :aria-pressed="isActiveField(option.orderBy.field)"
        class="focus-ring interactive-active gap-px-8 text-type-default duration-fast ease-standard flex w-full items-center rounded-md px-3 py-2 text-left transition-colors"
        :class="isActiveField(option.orderBy.field) ? 'bg-app-blue-200/20' : ''"
        @click="handleSelect(option)">
        <Star
          v-if="option.icon"
          class="icon-sm text-app-yellow-400 shrink-0"
          :stroke-width="1.5"
          fill="currentColor" />
        <span
          class="t-small flex-1 truncate text-inherit"
          :class="isActiveField(option.orderBy.field) ? 't-small-bold' : ''">
          {{ option.label }}
        </span>
        <ArrowDown
          v-if="isActiveField(option.orderBy.field) && value.orderBy.direction === Order_By.desc_nulls_last"
          :stroke-width="1.5"
          class="icon-sm text-app-blue-600 shrink-0"
          aria-hidden="true" />
        <ArrowUp
          v-else-if="isActiveField(option.orderBy.field) && value.orderBy.direction === Order_By.asc_nulls_last"
          :stroke-width="1.5"
          class="icon-sm text-app-blue-600 shrink-0"
          aria-hidden="true" />
      </button>
    </div>
  </Popover>
</template>
